import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  Tooltip,
} from '@chakra-ui/react';
import { useState } from 'react';
import { DotsVertical } from '@kula-ai/icons/outline/DotsVertical';
import { TableActionModal } from './TableActionModal';
import { KText } from '@/ui-library/components/text';

const renderMenuItem = (items, index, handleActionClick, selected) => {
  return (
    <Menu key={index}>
      <MenuButton>
        <Flex
          width="34px"
          height="34px"
          bg="white"
          borderRadius="8px"
          alignItems="center"
          justifyContent="center"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
        >
          <Icon
            as={DotsVertical}
            color="secondary-700"
            width="20px"
            height="20px"
          />
        </Flex>
      </MenuButton>
      <MenuList>
        {items.map(
          (item) =>
            item?.enabled && (
              <Tooltip
                {...item?.buttonProps?.disabledTooltipProps}
                shouldWrapChildren
                label={
                  (
                    typeof item?.buttonProps?.disabled === 'function'
                      ? item?.buttonProps?.disabled(selected)
                      : item?.buttonProps?.disabled
                  )
                    ? typeof item?.buttonProps?.disabledTooltip === 'function'
                      ? item?.buttonProps?.disabledTooltip(selected)
                      : item?.buttonProps?.disabledTooltip
                    : false
                }
                key={`tooltip-${item.id}`}
              >
                <MenuItem
                  key={item.id}
                  onClick={() => {
                    const isDisabled =
                      typeof item?.buttonProps?.disabled === 'function'
                        ? item?.buttonProps?.disabled(selected)
                        : item?.buttonProps?.disabled;
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    !isDisabled && handleActionClick(item);
                  }}
                  _hover={{ fontWeight: 'semibold' }}
                  data-test-id={item?.id}
                  opacity={
                    (
                      typeof item?.buttonProps?.disabled === 'function'
                        ? item?.buttonProps?.disabled(selected)
                        : item?.buttonProps?.disabled
                    )
                      ? 0.5
                      : 1
                  }
                  cursor={
                    (
                      typeof item?.buttonProps?.disabled === 'function'
                        ? item?.buttonProps?.disabled(selected)
                        : item?.buttonProps?.disabled
                    )
                      ? 'not-allowed'
                      : 'pointer'
                  }
                >
                  <Flex gap="0.5em" alignItems="center">
                    {item?.icon && (
                      <Icon as={item?.icon} {...item?.iconProps} />
                    )}
                    <KText
                      size="sm-semi-bold"
                      color="secondary-700"
                      fontWeight="inherit"
                    >
                      {item?.label}
                    </KText>
                  </Flex>
                </MenuItem>
              </Tooltip>
            )
        )}
      </MenuList>
    </Menu>
  );
};

/**
 * Renders a single bulk action button. Optional style overrides (only applied when present):
 * - enabledIconProps / disabledIconProps: merged with iconProps on the Icon
 * - enabledLabelProps / disabledLabelProps: when either is set, label is wrapped in Box with these props
 * Items without these props render as before (plain label, base iconProps only).
 */
const renderActionButton = (item, handleActionClick, selected) => {
  const isDisabled = item?.disabled
    ? true
    : typeof item?.buttonProps?.disabled === 'function'
      ? item?.buttonProps?.disabled(selected)
      : item?.buttonProps?.disabled;

  const hasCustomLabelStyle =
    item?.enabledLabelProps !== undefined ||
    item?.disabledLabelProps !== undefined;
  const iconProps = item?.icon
    ? {
        ...item?.iconProps,
        ...(isDisabled ? item?.disabledIconProps : item?.enabledIconProps),
      }
    : null;

  const labelProps = hasCustomLabelStyle
    ? ((isDisabled ? item?.disabledLabelProps : item?.enabledLabelProps) ?? {})
    : null;

  return (
    <Tooltip
      label={item?.tooltip}
      arrowSize={8}
      hasArrow
      placement="top"
      width="222px"
    >
      <Box>
        <Button
          key={item.id}
          disabled={isDisabled}
          onClick={() => handleActionClick(item)}
          variant="secondary"
          data-test-id={item.id}
          {...item.buttonProps}
        >
          {item?.icon && (
            <Icon mr="6px" as={item.icon} {...(iconProps || {})} />
          )}
          {hasCustomLabelStyle ? (
            <Box as="span" {...labelProps}>
              {item?.label}
            </Box>
          ) : (
            item?.label
          )}
        </Button>
      </Box>
    </Tooltip>
  );
};

export const TableBulkActions = ({
  selected = [],
  unSelected = [],
  toggleRowSelected,
  toggleAllRowsSelected,
  bulkActionMenuItems = [],
  showCount = true,
  totalCount = 0,
  resetSelectionValue = () => {},
}) => {
  const modalActions = useDisclosure();
  const [currentAction, setCurrentAction] = useState('');

  const handleActionClick = (item) => {
    setCurrentAction(item);
    if (item?.onClick) {
      item?.onClick(selected, unSelected);
    } else {
      modalActions?.onOpen();
    }
  };

  const renderActionItems = () => {
    return bulkActionMenuItems.map((item, index) => {
      if (Array.isArray(item) && item?.length > 0) {
        return renderMenuItem(item, index, handleActionClick, selected);
      } else {
        if (!item?.enabled) return null;
        return renderActionButton(item, handleActionClick, selected);
      }
    });
  };

  return (
    <>
      <Flex
        pos="fixed"
        bg="#FFFFFF"
        p="16px"
        borderRadius="8px"
        alignItems="center"
        bottom="10px"
        left="50%"
        transform="translateX(-50%)"
        boxShadow="0px 4px 20px rgba(0, 0, 0, 0.25)"
        zIndex="11"
      >
        {showCount && (
          <KText fontSize="sm" pr="12px" mr="12px">
            <KText size="sm-bold" color="secondary-900" as="span" pr="6px">
              {unSelected ? totalCount - unSelected.length : selected?.length}
            </KText>
            Selected
          </KText>
        )}
        <Box>
          <Flex
            alignItems="center"
            gap="0.375em"
            justifyContent="center"
            height="100%"
          >
            {renderActionItems()}
          </Flex>
        </Box>
      </Flex>
      {modalActions?.isOpen && (
        <TableActionModal
          modalActions={modalActions}
          selectedAction={currentAction}
          selected={selected}
          unSelected={unSelected}
          toggleAllRowsSelected={toggleAllRowsSelected}
          toggleRowSelected={toggleRowSelected}
          resetSelectionValue={resetSelectionValue}
        />
      )}
    </>
  );
};
